import React from "react";
import { ResponsiveBar } from "@nivo/bar";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
// A dictionary to translate negative sentinel -> user-friendly reason
const negativeReasons = {
  "-1": "Top-Bottom DAT",
  "-2": "Within-Group\nDAT (binary)",
  "-3": "N<10 DAT",
  "-4": "Within-Group\nDAT (non-binary)",
};

const ReusableNivoBarChart = ({
  data,
  keys,
  colors,
  titleText,
  indexBy,
  chartComparisonEnrollmentData,
  selectedComparisonGroup,
  selectedStudentGroup,
  theme,
  comparisonColor
}) => {
  /**
   * 1) Preprocess data:
   *    - If a value is negative, store that negative code in
   *      e.g. `row["Concentrator %_maskedReason"] = -3`,
   *      then set the numeric value to 0 so we avoid negative axis.
   */
  const processedData = data.map((row) => {
    const newRow = { ...row };
    keys.forEach((k) => {
      const val = row[k];
      if (typeof val === "number" && val < 0) {
        newRow[`${k}_maskedReason`] = val; // keep the negative code
        newRow[k] = 0; // force numeric value to zero for chart
      }
    });
    return newRow;
    
  });

  /**
   * 2) Format displayed label text:
   *    - Null or undefined => "No data"
   *    - Negative => "Masked" (we'll refine further below)
   *    - Positive => "99.9%"
   */
  const formatPercent = (value) => {
    if (value === null || value === undefined) {
      return "No data";
    }
    if (typeof value === "number" && value >= 0) {
      return `${value.toFixed(0)}%`;
    }
    // fallback
    return "Masked";
  };

  /**
   * 3) Splits text for axis labels / chart titles
   */
  const splitTextOnWords = (text, maxLength) => {
    if (!text) return [];
    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if ((currentLine + word).length > maxLength) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine += (currentLine ? " " : "") + word;
      }
    });
    if (currentLine) {
      lines.push(currentLine);
    }
    return lines;
  };

// Title layer for ReusableNivoBarChart
const titleLayer = (layerProps) => {
  const { width, margin } = layerProps;
  
  // The “chart area” excludes the margins
  const chartWidth = width - (margin.left + margin.right);

  // Decide how long each line can be before wrapping
  // Adjust the divisor to your preference
  const maxLineLength = Math.floor(chartWidth / 10);

  // Wrap text with your existing function
  const lines = splitTextOnWords(titleText, maxLineLength);

  // Dynamically pick a font size based on number of lines
  const fontSize = lines.length > 2 ? "0.85rem" : "1.25rem";

  // Horizontal center based on the chart area
  const xCenter = (chartWidth / 2) - (margin.left * .67);

  // Put the title near the top margin
  // margin.top is 120, so let's move the title ~20px below that
  const yPos = -(margin.top - 20);

  return (
    <g>
      <text
        x={xCenter}
        y={yPos}
        textAnchor="middle"
        style={{
          fontSize,
          fontWeight: "bold",
          fill: "#333333",
          fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        }}
      >
        {lines.map((line, i) => (
          <tspan
            key={i}
            x={xCenter}
            dy={i === 0 ? 0 : "1.2em"}
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

// Calculate the maximum Y-axis value
const maxDataValue = Math.max(
  ...data.map((d) =>
    keys.map((key) => d[key] || 0).reduce((a, b) => Math.max(a, b), 0)
  )
);

const maxComparisonValue = chartComparisonEnrollmentData
  ? Math.max(
      ...chartComparisonEnrollmentData.map((d) => d[`${selectedComparisonGroup} ${keys}`] || 0)
    )
  : 0;

const maxYValue = Math.ceil(Math.max(maxDataValue, maxComparisonValue) / 10) * 10; // Round up to the nearest 10


  // Custom layer for the comparison data
const comparisonLayer = ({ bars, yScale }) => {
  if (!chartComparisonEnrollmentData) return null;

  return (
    <g>
      {bars.map((bar, i) => {
        const comparisonDataPoint = chartComparisonEnrollmentData[i];
        // Build dynamic key using the first key in the array
        const dynamicKey = `${selectedComparisonGroup} ${keys[0]}`;
        const compValue = comparisonDataPoint && comparisonDataPoint[dynamicKey];
        const numerator = comparisonDataPoint && comparisonDataPoint.numerator;
        const denominator = comparisonDataPoint && comparisonDataPoint.denominator;
        const denominatorLabel = (comparisonDataPoint && comparisonDataPoint.denominatorLabel) || "students";
        const year = comparisonDataPoint && comparisonDataPoint.year;

        if (compValue == null) return null;

        const barY = bar.y; // Y-position of the bar
        const comparisonY = yScale(compValue); // Y-position of the dashed line
        const barHeight = bar.height; // Height of the bar
        const rectHeight = 18; // Height of the visible rect
        const hoverPadding = 10; // Extra padding for the hover area
        const offsetAboveLine = -1; // Move everything above the dashed line

        // Determine overlap: Comparison hover area intersects with the bar
        const overlap = comparisonY < barY + barHeight;

        return (
          <Tippy
            key={`comparison-line-${i}`}
            interactive={false}
            delay={[500, 0]} // 500ms delay before showing the tooltip
            placement="right"
            offset={[-50, 7]} // Adjust positioning relative to the cursor
            appendTo={document.body} // Ensures it's rendered in the body
            theme={theme.comparison}
            content={
              <div
                style={{ border: `1px solid ${comparisonColor}` }}
                className="bg-white rounded-lg shadow-lg p-4 max-w-xs text-sm text-gray-800 leading-relaxed"
              >
                <div className="mb-2 flex items-center">
                  <strong
                    style={{
                      color: comparisonColor,
                      fontSize: "18px",
                      marginRight: "8px",
                    }}
                  >
                    {selectedComparisonGroup}
                  </strong>
                </div>
                <div>
                  <div className="mb-2 flex items-center">
                    <span>{formatPercent(compValue)} Participation</span>
                  </div>
                  In {year}, <strong>{numerator}</strong> out of{" "}
                  <strong>{denominator}</strong> {denominatorLabel} (<em>
                    {formatPercent(compValue)}
                  </em>) participated.
                </div>
              </div>
            }
          >
            <g>
              {/* Invisible hover area */}
              <rect
                x={bar.x - 10} // Start of the dashed line
                y={
                  overlap
                    ? comparisonY - rectHeight - offsetAboveLine
                    : comparisonY - rectHeight - hoverPadding / 2 - offsetAboveLine
                }
                width={bar.width + 20} // Covers the entire width of the dashed line
                height={overlap ? rectHeight : rectHeight + hoverPadding} // Adjust height if overlapping
                fill="transparent" // Invisible but interactive
                className="cursor-pointer"
              />
              {/* Dashed line */}
              <line
                x1={bar.x - 10}
                x2={bar.x + bar.width + 10}
                y1={comparisonY}
                y2={comparisonY}
                stroke={comparisonColor}
                strokeWidth={2}
                strokeDasharray="6,3"
              />
              {/* Visible Rect */}
              <rect
                x={bar.x + bar.width - 20}
                y={comparisonY - rectHeight - offsetAboveLine} // Move rect above the line
                width={29}
                height={rectHeight}
                rx={2}
                fill={comparisonColor}
              />
              {/* Label */}
              <text
                x={bar.x + bar.width - 5}
                y={comparisonY - offsetAboveLine - rectHeight / 4} // Adjust text to align with rect
                textAnchor="middle"
                style={{
                  fill: "#FFF",
                  fontSize: "0.75rem",
                  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                }}
                className="fill-white text-xs font-bold"
              >
                {formatPercent(compValue)}
              </text>
            </g>
          </Tippy>
        );
      })}
    </g>
  );
};


// Custom tooltip layer for the bars
const tooltipLayer = ({ bars }) => {
  return (
    <g>
      {bars.map((bar, index) => {
        const { numerator, denominator, denominatorLabel, year, studentGroup } = bar.data.data || {};
        const value = bar.data.value;
        const color = bar.color; // Assuming bar.color contains the color for the bar
        // Generate a unique key using the index (fallback)
        const uniqueKey = `tooltip-bar-${studentGroup}${year}${index}`;
        return (
          <Tippy
          key={uniqueKey}
            interactive={false}
            delay={[500, 0]} // 500ms delay before showing the tooltip
            placement="top"
            offset={[0, 27]} // Adjust positioning relative to the cursor
            appendTo={document.body} // Ensures it's rendered in the body
            theme={theme.primary}
            content={
              <div          style={{
                border: `1px solid ${color}`,
              }} className="bg-white rounded-lg shadow-lg p-4 max-w-xs text-sm text-gray-800 leading-relaxed">
                <div className="mb-2 flex items-center">
                <strong style={{ color, fontSize: "18px", marginRight: "8px" }}>
              {selectedStudentGroup}
            </strong>
                </div>
                <div>
                  <div className="mb-2 flex items-center">
                    <span>{formatPercent(value)} Participation</span>
                  </div>
                  In {year}, <strong>{numerator}</strong> out of{" "}
                  <strong>{denominator}</strong> {denominatorLabel || "students"} (<em>
                    {formatPercent(value)}
                  </em>) participated.
                </div>
              </div>
            }
          >
            <rect
              x={bar.x}
              y={bar.y}
              width={bar.width}
              height={bar.height}
              fill="transparent"
              className="cursor-pointer"
            />
          </Tippy>
        );
      })}
    </g>
  );
};

  return (
      <div className="w-full h-[360px] mx-auto">
        <ResponsiveBar
          data={processedData}
          keys={keys}
          indexBy={indexBy}
          maxValue={maxYValue}
          isInteractive={false}
          layout="vertical"
          margin={{ top: 120, right: 0, bottom: 40, left: 80 }}
          padding={0.3}
          innerPadding={3}
          groupMode="grouped"
          colors={({ id }) => colors[id]}
          colorBy="id"
          borderWidth={3}
          borderRadius={3}
          borderColor={{
            from: "color",
            modifiers: [["brighter", "0.3"]],
          }}
          axisBottom={{
            tickSize: 5,
            
            tickPadding: 5,
            tickRotation: 0,
            tickValues: processedData.map((d) => d[indexBy]),
            renderTick: (props) => {
              const { value, x, y } = props;
              const lines = splitTextOnWords(value, 20);
              return (
                <g transform={`translate(${x},${y + 22})`}>
                  {lines.map((line, index) => (
                    <text
                      key={index}
                      x={0}
                      y={index * 15}
                      textAnchor="middle"
                      style={{
                        fill: "#333333",
                        fontSize: "0.875rem",
                        fontFamily:
                          "Helvetica Neue, Helvetica, Arial, sans-serif",
                      }}
                    >
                      {line}
                    </text>
                  ))}
                </g>
              );
            },
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Percentage (%)",
            legendPosition: "middle",
            legendOffset: -60,
            tickValues: 5,
          }}
          label={null}
          theme={{
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            grid: {
              line: {
                stroke: "#a3a3a3",
                strokeWidth: 1,
                strokeDasharray: "6,4",
              },
            },
            axis: {
              domain: {
                line: {
                  stroke: "#777777",
                  strokeWidth: 1,
                },
              },
              ticks: {
                line: {
                  stroke: "#777777",
                  strokeWidth: 1,
                },
                text: {
                  fill: "#333333",
                  fontSize: "0.875rem",
                  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                },
              },
              legend: {
                text: {
                  fill: "#333333",
                  fontSize: "1rem",
                  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
                },
              },
            },
            legends: {
              text: {
                fill: "#333333",
                fontSize: "0.875rem",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              },
            },
          }}
          legends={[
            {
              dataFrom: "keys",
              anchor: "top",
              direction: "row",
              justify: false,
              translateX: -60,
              translateY: -50,
              itemsSpacing: 8,
              itemWidth: 150,
              itemHeight: 20,
              itemDirection: "left-to-right",
              itemOpacity: 0.85,
              symbolSize: 20,
              symbolShape: "circle",
            },
          ]}
          layers={[
            titleLayer,
            
            // 2) Custom Grid Lines Layer
            ({ yScale, width }) => {
              const ticks = yScale.ticks ? yScale.ticks(5) : yScale.domain();

              return (
                <g>
                  {ticks.map((tick, i) => (
                    <line
                      key={i}
                      x1={0}
                      x2={width}
                      y1={yScale(tick)}
                      y2={yScale(tick)}
                      stroke="#a3a3a3"
                      strokeWidth={0.5}
                      strokeDasharray="6,4"
                    />
                  ))}
                </g>
              );
            },
            // 3) Custom layer for the bars themselves
            "bars",
           // 4) A custom layer for the pattern <defs> (so it's available for masked bars)
  () => (
    <defs>
      <pattern
        id="maskDiagonal"
        patternUnits="userSpaceOnUse"
        width="6"
        height="6"
        patternTransform="rotate(45)"  
      >
        {/* This white rect is the background of the pattern tile */}
        {/* <rect x="0" y="0" width="6" height="6" fill="white" /> */}

        {/* A single diagonal line repeating every 6px */}
        <line
          x1="0"
          y1="0"
          x2="0"
          y2="6"
          stroke="#e4e4e7"
          strokeWidth="1.5"
        />
      </pattern>
    </defs>
  ),

  // 5) Custom layer for masked placeholders & labels
  ({ bars, yScale }) => {
    // figure out the maximum value in the yScale domain
    // e.g., if domain is [0, 100], maxVal = 100
    const domain = yScale.domain();
    const maxVal = Math.max(...domain);

    return bars.map((bar) => {
      const reasonKey = `${bar.data.id}_maskedReason`;
      const maskedVal = bar.data.data[reasonKey]; // e.g. -1, -2, etc.
      const numericVal = bar.data.value;          // e.g. 0 if negative originally

      let labelText;
      if (maskedVal !== undefined) {
        // We have a negative code
        const friendlyReason =
          negativeReasons[String(maskedVal)] || "Masked";
        // Example multiline label:
        // "Masked - Top-Bottom DAT"
        // "(-1)"
        labelText = `Masked\n${friendlyReason}`;
      } else {
        // Normal bar
        labelText = formatPercent(numericVal);
      }

      // If masked => draw a full bar with diagonal pattern
      if (maskedVal !== undefined) {
        // We want to draw a bar from "0%" to "maxVal%".
        // Because Nivo draws from top (yScale(maxVal)) down to bottom (yScale(0)).
        const barY = yScale(maxVal);
        const barHeight = yScale(0) - barY;

        // We'll center the text in this bar:
        const centerX = bar.x + bar.width / 2;
        const centerY = barY + barHeight / 2;

        // For multiline label, we can split on `\n`
        const lines = labelText.split("\n");

        return (
          <g key={bar.key}>
            {/* full-height rectangle */}
            <rect
              x={bar.x}
              y={barY}
              width={bar.width}
              height={barHeight}
              fill="url(#maskDiagonal)"  // Use the diagonal pattern
              fillOpacity={1}         // Slightly visible
              stroke="#e4e4e7"
              strokeWidth={1}
            />
            {/* multiline masked label, centered */}
            <text
              x={centerX}
              y={centerY}
              textAnchor="middle"
              alignmentBaseline="middle"
              style={{
                fill: "#71717a",
                fontWeight: "semibold",
                fontStyle: "italic",
                fontSize: "0.7rem",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              }}
            >
              {lines.map((line, i) => (
                <tspan
                  key={i}
                  x={centerX}
                  dy={i === 0 ? 0 : "1.2em"}
                >
                  {line}
                </tspan>
              ))}
            </text>
          </g>
        );
      } else {
        // Normal bar => just draw a label above it
        return (
          <g key={bar.key}>
            <rect
              x={bar.x + bar.width / 2 - 21}
              y={bar.y - 18}
              width={44}
              height={16}
              fill="#ffffff"
            />
            <text
              x={bar.x + bar.width / 2}
              y={bar.y - 6}
              textAnchor="middle"
              style={{
                fill: "#4B5563",
                fontWeight: "bold",
                fontSize: "0.875rem",
                fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              }}
            >
              {labelText}
            </text>
          </g>
        );
      }
    });
  },
            "axes",
            "markers",
            "legends",
            tooltipLayer,
            comparisonLayer
            
          ]}
        />
      </div>
  );
};

export default ReusableNivoBarChart;